import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Loader2, Users, X, AlertTriangle, Unplug } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { isAdminOrOps } from '../utils/roles';
import { apiClient } from '../api/client';
import slackColorIcon from '../assets/icons/slack-color.png';

interface SlackIntegrationStatus {
  connected: boolean;
  team_name?: string;
  team_id?: string;
  installed_by?: string;
  installed_at?: number;
}

interface ToastMessage {
  type: 'success' | 'error';
  message: string;
}

const SlackIntegration: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [status, setStatus] = useState<SlackIntegrationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      fetchStatus();
    }
  }, [authLoading, user]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const data = await apiClient.getSlackIntegrationStatus();
      setStatus(data);
    } catch (error) {
      console.error('Failed to fetch Slack status:', error);
      setToast({ type: 'error', message: 'Failed to load Slack integration status' });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const data = await apiClient.getSlackInstallUrl();
      
      // Open Slack OAuth in popup
      const width = 600;
      const height = 800;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      window.open(
        data.install_url,
        'Slack Authorization',
        `width=${width},height=${height},left=${left},top=${top}`
      );
      
      // Poll for connection status
      const pollInterval = setInterval(async () => {
        try {
          const statusData = await apiClient.getSlackIntegrationStatus();
          
          if (statusData.connected) {
            clearInterval(pollInterval);
            setStatus(statusData);
            setToast({ type: 'success', message: `Connected to Slack workspace: ${statusData.team_name}` });
          }
        } catch (err) {
          // Ignore polling errors
        }
      }, 2000);
      
      // Stop polling after 2 minutes
      setTimeout(() => clearInterval(pollInterval), 120000);
      
    } catch (error) {
      console.error('Failed to initiate Slack connection:', error);
      setToast({ type: 'error', message: 'Failed to connect Slack workspace' });
    }
  };

  const confirmDisconnect = async () => {
    try {
      setDisconnecting(true);
      await apiClient.disconnectSlackWorkspace();
      setStatus({ connected: false });
      setToast({ type: 'success', message: 'Slack workspace disconnected' });
      setDisconnectDialogOpen(false);
    } catch (error) {
      console.error('Failed to disconnect Slack:', error);
      setToast({ type: 'error', message: 'Failed to disconnect Slack' });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSyncUsers = async () => {
    try {
      setSyncing(true);
      await apiClient.syncSlackUsers();
      setToast({ type: 'success', message: 'User sync started. This may take a few minutes.' });
    } catch (error) {
      console.error('Failed to sync Slack users:', error);
      setToast({ type: 'error', message: 'Failed to sync Slack users' });
    } finally {
      setSyncing(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Check authorization
  if (!isAdminOrOps(user?.role)) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Access denied. Only organization administrators or platform administrators can configure Slack integration.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast notification */}
      {toast && (
        <Alert className={toast.type === 'error' ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950' : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'}>
          <div className="flex items-center justify-between">
            <AlertDescription className={toast.type === 'error' ? 'text-red-900 dark:text-red-100' : 'text-green-900 dark:text-green-100'}>
              {toast.message}
            </AlertDescription>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setToast(null)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Alert>
      )}

      {status?.connected ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={slackColorIcon} alt="Slack" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  Slack
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                </CardTitle>
                <CardDescription>Real-time incident notifications, AI-powered Q&A, and workflow messages directly in Slack.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              {status.team_name && (
                <div>
                  <span className="text-muted-foreground">Workspace</span>
                  <p className="font-medium">{status.team_name}</p>
                </div>
              )}
              {status.team_id && (
                <div>
                  <span className="text-muted-foreground">Team ID</span>
                  <p className="font-medium">{status.team_id}</p>
                </div>
              )}
              {status.installed_at && (
                <div>
                  <span className="text-muted-foreground">Installed</span>
                  <p className="font-medium">{new Date(status.installed_at * 1000).toLocaleDateString()}</p>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleSyncUsers} disabled={syncing} className="gap-1">
                {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-3.5 h-3.5" />}
                Sync Users
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDisconnectDialogOpen(true)} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={slackColorIcon} alt="Slack" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>Connect Slack</CardTitle>
                <CardDescription>Connect your Slack workspace to enable real-time incident notifications, AI-powered Q&A, and workflow messages in Slack.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">1</span>
                <span>Click <strong className="text-foreground">Connect Slack Workspace</strong> below</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">2</span>
                <span>Authorize <strong className="text-foreground">Kestrel AI</strong> in your Slack workspace</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">3</span>
                <span>Invite the bot to your <strong className="text-foreground">#incidents</strong> channel</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-medium mt-0.5">4</span>
                <span>You'll start receiving incident notifications automatically</span>
              </li>
            </ol>

            <Button onClick={handleConnect}>
              Connect Slack Workspace
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Disconnect Slack Workspace?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately stop all incident notifications to Slack. Your team will no longer receive:
              <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                <li>Real-time incident alerts</li>
                <li>AI-powered Q&A in threads</li>
                <li>Namespace owner @mentions</li>
              </ul>
              <strong className="block mt-3">You can reconnect at any time.</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDisconnect}
              disabled={disconnecting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {disconnecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect Slack'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SlackIntegration;

